import { useCallback, useState } from "react";
import { useReactMediaRecorder } from "react-media-recorder";

const useCameraRecorder = () => {
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState<string | null>(null);

  const {
    status,
    startRecording,
    stopRecording: stopRawRecording,
    error,
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    askPermissionOnMount: false,
    onStop: (blobUrl) => setRecordedVideoUrl(blobUrl),
  });

  // Preview camera
  const startPreview = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640, max: 640 },
          height: { ideal: 360, max: 360 },
          frameRate: { ideal: 15, max: 15 },
        },
        audio: false,
      });
      setPreviewStream(stream);
    } catch (err: any) {
      setPreviewStream(null);
    }
  }, []);

  const stopPreview = useCallback(() => {
    if (previewStream) {
      previewStream.getTracks().forEach((track) => track.stop());
      setPreviewStream(null);
    }
  }, [previewStream]);

  const stopRecording = useCallback(async () => {
    await stopRawRecording();
    stopPreview();
  }, [stopRawRecording, stopPreview]);

  const resetRecordedVideoUrl = () => {
    if (recordedVideoUrl) {
      URL.revokeObjectURL(recordedVideoUrl); // cleanup blob
    }
    setRecordedVideoUrl(null);
  };

  return {
    stream: previewStream || null,
    startPreview,
    stopPreview,
    startRecording,
    stopRecording,
    recordedVideoUrl,
    resetRecordedVideoUrl,
    isRecording: status === "recording",
    error,
    permissionState: status,
  };
};

export default useCameraRecorder;
